import { RegisterClass } from '@memberjunction/global';
import { BaseApplication, NavItem } from '@memberjunction/ng-base-application';
import { IzzyMetadataEngine } from 'mj_generatedentities';
import { BC_ROLE_LEVELS } from '@bluecypress/bcsaas-core';

/**
 * Minimum organization plan level for the Ticket Management module (Pro = 20).
 * Mirrors TICKET_MANAGEMENT_MIN_PLAN_LEVEL in ProcessorAgent so the Tickets nav
 * tab uses the same paid-add-on floor the server enforces.
 */
const TICKET_MANAGEMENT_MIN_PLAN_LEVEL = 20;

@RegisterClass(BaseApplication, 'IzzyApplication')
export class IzzyApplication extends BaseApplication {
  override async GetNavItems(): Promise<NavItem[]> {
    if (!this.DefaultNavItems) {
      return [];
    }

    let items: NavItem[];
    try {
      items = JSON.parse(this.DefaultNavItems) as NavItem[];
    } catch (e) {
      console.error(`Failed to parse DefaultNavItems for ${this.Name}:`, e);
      return [];
    }

    const isAdmin = await this.hasAdminMembershipInAnyOrg();
    if (!isAdmin) {
      items = items.filter(item => item.Label !== 'Administration');
    }

    const ticketingEnabled = await this.ticketingEnabledForAnyOrg();
    if (!ticketingEnabled) {
      items = items.filter(item => item.Label !== 'Tickets');
    }

    return items;
  }

  /**
   * True iff the Ticket Management module is usable for ANY organization the
   * current user belongs to — i.e. that org is on a Pro+ plan AND has
   * TicketManagementEnabled set. Mirrors the deterministic server-side gate in
   * ProcessorAgent.IsTicketAutoCreateEnabled (plan level + setting), so the
   * Tickets nav tab only appears when tickets can actually exist.
   *
   * This is a UX hint — the ticket workspace and the ticket-creation pipeline
   * both re-enforce the gate server-side; the trust boundary lives there.
   */
  private async ticketingEnabledForAnyOrg(): Promise<boolean> {
    try {
      await IzzyMetadataEngine.Instance.Config(false);
    } catch {
      // Pre-login boot may call GetNavItems before Metadata.Provider is ready.
      // Fail open: the workspace + pipeline gates enforce the real check.
      return true;
    }

    const engine = IzzyMetadataEngine.Instance;
    const personId = engine.CurrentContactID; // legacy name; v1.3 maps to PersonID
    if (!personId) return false;

    // Orgs the user actively belongs to. Prefer the middleware-stamped tenant
    // context — the guaranteed-complete membership source under BCSaaS v1.5
    // tenant filtering (RunView-based sources are filtered for non-system users).
    const stamped = engine.CurrentUserTenantMemberships;
    const orgIds = new Set(
      stamped
        ? stamped.map(m => m.organizationID)
        : engine.OrganizationContacts
            .filter(opr => opr.PersonID === personId && opr.Status === 'Active')
            .map(opr => opr.OrganizationID)
    );

    for (const orgId of orgIds) {
      // Plan gate: Ticket Management is a Pro+ (Level >= 20) add-on.
      const plan = engine.GetOrganizationPlan(orgId);
      if (!plan || (plan.Level ?? 0) < TICKET_MANAGEMENT_MIN_PLAN_LEVEL) {
        continue;
      }
      // Setting gate: resolve TicketManagementEnabled via any channel of this org
      // (channel-scope falls back to org-scope, so any channel resolves the same
      // org-level value). Default false when unset.
      const channel = engine.Channels.find(c => c.OrganizationID === orgId);
      if (!channel) {
        continue;
      }
      if (engine.GetSettingValueBoolean('TicketManagementEnabled', channel.ID, false)) {
        return true;
      }
    }

    return false;
  }

  /**
   * True iff the current user holds an active membership at Level >= ADMIN in
   * any organization.
   *
   * Uses the OrganizationPersonRole cache on IzzyMetadataEngine — each OPR row
   * carries Level (snapshot, captured at membership creation in v1.3.x) and
   * Status, so we can answer this without a per-org server query. Nav re-renders
   * frequently; the cache lookup is O(n) over the user's memberships (typically
   * single-digit rows).
   *
   * This is a UX hint — the Administration page itself re-checks server-side
   * via BCMembershipService.HasMembershipLevel on load, and the underlying
   * queries are RLS-enforced. The trust boundary lives in the DB, not here.
   */
  private async hasAdminMembershipInAnyOrg(): Promise<boolean> {
    try {
      await IzzyMetadataEngine.Instance.Config(false);
    } catch {
      // Pre-login boot can invoke GetNavItems before Metadata.Provider is
      // populated. Fail open: page-level gate enforces the real check.
      return true;
    }

    const engine = IzzyMetadataEngine.Instance;
    const personId = engine.CurrentContactID; // legacy name; v1.3 maps to PersonID
    if (!personId) return false;

    // Delegates to the engine, which prefers the middleware-stamped tenant
    // context (guaranteed complete under BCSaaS v1.5 tenant filtering) and
    // falls back to the OPR cache when unavailable.
    return engine.CurrentUserHasMembershipLevel(BC_ROLE_LEVELS.ADMIN);
  }
}

export function LoadIzzyApplication(): void {}
